import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  ButtonVariant,
  Spinner,
  Text,
  TextField,
} from "@merge-api/merge-javascript-shared";
import { Plus } from "lucide-react";
import { Dialog } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Dialog";
import { CONDUCTOR_FLAGS_PATH } from "../../../router/RouterUtils";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorPost } from "../conductorApi";
import { useConductorResource } from "../hooks/useConductorResource";
import ConductorFlagDetail from "./components/ConductorFlagDetail";
import ConductorFlagsTable from "./components/ConductorFlagsTable";
import { ConductorFlag } from "./types";

// Conductor flags are global rather than integration-scoped, so this is a
// top-level page. The same component serves the index and one flag's detail,
// switching on the route param.
const ConductorFlagsPage = () => {
  // state
  const [reloadToken, setReloadToken] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // hooks
  const history = useHistory();
  const { flagId } = useParams<{ flagId?: string }>();
  const { data: flags, isLoading } = useConductorResource<ConductorFlag[]>(
    flagId ? null : "/conductor/flags",
    "Error fetching flags",
    reloadToken
  );

  // event handlers
  const openFlag = (id: string) => history.push(`${CONDUCTOR_FLAGS_PATH}/${id}`);

  const create = async () => {
    setIsSaving(true);
    // Create rejects a body carrying an id, and a taken name comes back as a
    // 409 rather than overwriting, so no client-side collision check is needed.
    const { ok, data, error } = await conductorPost<ConductorFlag>("/conductor/flags", {
      name: newName.trim(),
      everyone: null,
      rules: [],
      note: "",
    });
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Flag created");
      setIsCreating(false);
      setNewName("");
      if (data?.id) openFlag(data.id);
      else setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Failed to create flag");
    }
  };

  // render
  if (flagId) {
    return (
      <>
        <Button
          size="sm"
          variant={ButtonVariant.TextBlue}
          onClick={() => history.push(CONDUCTOR_FLAGS_PATH)}
        >
          Back to flags
        </Button>
        <div className="mt-4">
          <ConductorFlagDetail
            flagId={flagId}
            onDeleted={() => history.push(CONDUCTOR_FLAGS_PATH)}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Text variant="h5">Flags</Text>
          <Text variant="sm" className="mt-1 block text-gray-60">
            Feature flags owned by the conductor service, separate from MergeFlag and waffle
            switches
          </Text>
        </div>
        <Button size="sm" leftIcon={<Plus size={12} />} onClick={() => setIsCreating(true)}>
          New flag
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <ConductorFlagsTable flags={flags || []} onOpen={openFlag} />
      )}

      <Dialog
        open={isCreating}
        onClose={() => setIsCreating(false)}
        variant="sm"
        title="New flag"
        primaryButtonText="Create flag"
        primaryButtonLoading={isSaving}
        primaryButtonDisabled={newName.trim() === "" || isSaving}
        onPrimaryButtonClick={create}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsCreating(false)}
      >
        <TextField
          labelTitle="Name"
          labelSubtitle="The lookup key conductor evaluates. Must be unique."
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="conductor_my_feature"
        />
      </Dialog>
    </>
  );
};

export default ConductorFlagsPage;
